import { useState } from "react";

import { Loader2, TrendingUp, AlertCircle, CheckCircle2, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";

interface QualityScore {
  overall: number;
  completeness: number;
  clarity: number;
  engagement: number;
  recommendations: string[];
  strengths: string[];
}

interface BoardQualityScoreProps {
  boardName: string;
  description: string;
  personas: Array<{
    role_name: string;
    mission: string;
    principles: string;
    competencies: string;
    key_relationships: string;
    typical_questions: string | string[];
  }>;
}

export const BoardQualityScore = ({ boardName, description, personas }: BoardQualityScoreProps) => {
  const { execute: invoke, isLoading } = useEdgeFunction({ auth: true });
  const [score, setScore] = useState<QualityScore | null>(null);

  const analyzeQuality = async () => {
    const { data, error } = await invoke("score-board-quality", {
      body: { boardName, description, personas },
      showErrorToast: true,
    });

    if (error) return;
    
    setScore(data);
    toast.success("Quality analysis complete!");
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBadge = (score: number) => {
    if (score >= 80) return { label: "Excellent", variant: "default" as const };
    if (score >= 60) return { label: "Good", variant: "secondary" as const };
    return { label: "Needs Work", variant: "destructive" as const };
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Content Quality Score
            </CardTitle>
            <CardDescription>
              AI-powered analysis of board completeness, clarity, and engagement
            </CardDescription>
          </div>
          <Button
            onClick={analyzeQuality}
            disabled={isLoading || !personas.length}
            size="sm"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4 mr-2" />
                Analyze
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {score && (
        <CardContent className="space-y-6">
          {/* Overall Score */}
          <div className="text-center space-y-2">
            <div className={`text-5xl font-bold ${getScoreColor(score.overall)}`}>
              {score.overall}
            </div>
            <Badge {...getScoreBadge(score.overall)}>{getScoreBadge(score.overall).label}</Badge>
          </div>

          {/* Individual Metrics */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Completeness</span>
                <span className={`text-sm font-bold ${getScoreColor(score.completeness)}`}>
                  {score.completeness}%
                </span>
              </div>
              <Progress value={score.completeness} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Clarity</span>
                <span className={`text-sm font-bold ${getScoreColor(score.clarity)}`}>
                  {score.clarity}%
                </span>
              </div>
              <Progress value={score.clarity} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Engagement</span>
                <span className={`text-sm font-bold ${getScoreColor(score.engagement)}`}>
                  {score.engagement}%
                </span>
              </div>
              <Progress value={score.engagement} />
            </div>
          </div>

          {/* Strengths */}
          {score.strengths.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Strengths
              </h4>
              <ul className="space-y-1">
                {score.strengths.map((strength, index) => (
                  <li key={index} className="text-sm text-muted-foreground pl-6">
                    • {strength}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {score.recommendations.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                Recommendations
              </h4>
              <ul className="space-y-1">
                {score.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-muted-foreground pl-6">
                    • {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
